import { BtnAccent } from '../ui/BtnAccent'
import { BtnGhost } from '../ui/BtnGhost'
import { SectionLabel } from '../ui/SectionLabel'

const cx = 'max-w-[1100px] mx-auto px-5 sm:px-8'

const CREDIT_WEIGHTS = [
  { type: 'Guion YouTube largo (≥8 min)', credits: 4 },
  { type: 'Guion YouTube medio (<8 min)', credits: 2 },
  { type: 'Short · LinkedIn · Regenerar sección', credits: 1 },
]

const PLAN_EQUIVALENTS = [
  {
    plan: 'Free (10 créditos)',
    eq: '10 Shorts, o 2 guiones largos + regen, o 5 guiones medios',
  },
  {
    plan: 'Pro (200 créditos)',
    eq: '~50 guiones largos/mes, o mezcla libre. Sobra para canal semanal.',
  },
]

const PLANS = [
  {
    name: 'Free — Para empezar',
    price: '$0',
    period: '/mes',
    desc: '10 créditos al mes. Sin rollover. Sin tarjeta de crédito.',
    accent: false,
    yes: [
      '10 créditos/mes',
      'Si falla la generación, crédito devuelto',
      'Analytics últimos 28 días',
      '1 canal de YouTube',
      'Kanban y calendario',
    ],
    no: [
      'Curva de retención',
      'Fuentes de tráfico',
      'Plantilla narrativa avanzada',
      'Sin marca de agua',
    ],
    cta: 'Empezar gratis',
  },
  {
    name: 'Pro — Para creadores en serio',
    price: '$29',
    period: '/mes',
    desc: '200 créditos/mes · rollover 60 días · o $19/mes anual. Reembolso 14 días sin preguntas.',
    accent: true,
    yes: [
      '200 créditos/mes + rollover 60 días',
      'API key propia (BYOK) — generación ilimitada',
      'Packs extra: 20/60/150 créditos ($4 / $9 / $19)',
      'Analytics completas de YouTube',
      'Curva de retención',
      'Fuentes de tráfico',
      'Hasta 3 canales conectados',
      'Plantilla narrativa avanzada',
      'Sin marca de agua en exports',
    ],
    no: [],
    cta: 'Actualizar a Pro →',
  },
]

export function Pricing() {
  return (
    <section id="precios" className="py-24 border-b border-ds-border">
      <div className={cx}>
        <SectionLabel className="mb-4">Precios</SectionLabel>
        <h2 className="font-display font-black text-display-md tracking-tight2 uppercase text-ds-text">
          Simple.
          <br />
          Sin sorpresas.
        </h2>
        <p className="font-sans text-[14px] text-ds-muted mt-4 mb-10 max-w-[420px] leading-[1.6]">
          Los primeros 50 usuarios tienen el precio de lanzamiento bloqueado para siempre.
        </p>

        {/* Credit translation block — defuses "me voy a quemar los créditos" objection */}
        <div className="mb-10 border border-ds-border-mid">
          <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr] bg-ds-surface">
            <div className="p-6 sm:p-7 border-b md:border-b-0 md:border-r border-ds-border-mid">
              <p className="font-display font-bold text-label tracking-label uppercase text-ds-muted mb-4">
                Qué te cuesta cada cosa
              </p>
              <ul className="flex flex-col gap-2.5 list-none p-0 m-0">
                {CREDIT_WEIGHTS.map(({ type, credits }) => (
                  <li key={type} className="flex items-start justify-between gap-4">
                    <span className="font-sans text-[13px] text-ds-text leading-[1.5]">{type}</span>
                    <span className="font-display font-black text-[14px] tracking-[-0.01em] text-ds-red flex-shrink-0">
                      {credits} créd.
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-6 sm:p-7 bg-ds-bg">
              <p className="font-display font-bold text-label tracking-label uppercase text-ds-muted mb-4">
                Lo que te da cada plan
              </p>
              <ul className="flex flex-col gap-3 list-none p-0 m-0">
                {PLAN_EQUIVALENTS.map(({ plan, eq }) => (
                  <li key={plan}>
                    <p className="font-display font-extrabold text-[13px] tracking-[0.02em] uppercase text-ds-text mb-1">
                      {plan}
                    </p>
                    <p className="font-sans text-[12px] text-ds-muted leading-[1.55]">{eq}</p>
                  </li>
                ))}
              </ul>
              <p className="font-sans italic text-[12px] text-ds-muted mt-5 pt-4 border-t border-ds-border leading-[1.5]">
                Odiamos los créditos tanto como tú. Por eso ruedan 60 días en Pro y si falla la
                generación se devuelve solo.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-[1px] bg-ds-border border border-ds-border">
          {PLANS.map(({ name, price, period, desc, accent, yes, no, cta }) => (
            <div key={name} className={`p-8 sm:p-12 ${accent ? 'bg-ds-surface' : 'bg-ds-bg'}`}>
              <p className="font-display font-bold text-label tracking-label uppercase text-ds-muted mb-5">
                {name}
              </p>
              <div className="mb-1">
                <span className="font-display font-black text-[80px] tracking-[-0.03em] leading-none text-ds-text">
                  {price}
                </span>
                <span className="font-display font-semibold text-[16px] text-ds-muted">
                  {period}
                </span>
              </div>
              <p className="font-sans text-[13px] text-ds-muted mt-3 mb-10 leading-[1.6]">{desc}</p>

              <ul className="flex flex-col gap-3 mb-10 list-none p-0 m-0">
                {yes.map(f => (
                  <li key={f} className="flex items-start gap-2.5">
                    <span
                      className={`font-display font-bold flex-shrink-0 ${accent ? 'text-ds-red' : 'text-ds-muted'}`}
                    >
                      —
                    </span>
                    <span
                      className={`font-sans text-[13px] leading-[1.5] ${accent ? 'text-ds-text' : 'text-[#999]'}`}
                    >
                      {f}
                    </span>
                  </li>
                ))}
                {no.map(f => (
                  <li key={f} className="flex items-start gap-2.5 opacity-30">
                    <span className="font-display font-bold text-ds-border-mid flex-shrink-0">
                      —
                    </span>
                    <span className="font-sans text-[13px] text-ds-muted leading-[1.5]">{f}</span>
                  </li>
                ))}
              </ul>

              {accent ? (
                <BtnAccent to="/auth" className="block text-center">
                  {cta}
                </BtnAccent>
              ) : (
                <BtnGhost to="/auth" className="block text-center">
                  {cta}
                </BtnGhost>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
